import { AlertTriangle } from 'lucide-react';

interface RiskBreakdownProps {
    breakdown: Record<string, number>;
    topFactors: Record<string, number>;
}

export function RiskBreakdown({ breakdown, topFactors }: RiskBreakdownProps) {
    const labels: Record<string, string> = {
        latency_risk: 'Latency Issues',
        state_entropy: 'Event Disorder',
        retry_risk: 'High Retries',
        historical_risk: 'Historical Loss',
        amount_risk: 'Transaction Size',
        time_risk: 'Time of Day',
        provider_risk: 'Provider Issues',
        pattern_risk: 'Pattern Match',
    };

    const topKeys = Object.keys(topFactors);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Risk Factor Breakdown</h4>
                <span className="text-xs text-gray-500">0-100% scale</span>
            </div>

            {Object.entries(breakdown).map(([key, value]) => {
                const isTopFactor = topKeys.includes(key);
                return (
                    <div key={key} className="flex items-center gap-3">
                        <div className="w-32 text-sm text-gray-600 flex items-center gap-1">
                            {labels[key] || key}
                            {isTopFactor && (
                                <AlertTriangle size={12} className="text-orange-500" />
                            )}
                        </div>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${value >= 0.7 ? 'bg-red-500' :
                                    value >= 0.5 ? 'bg-orange-500' :
                                        value >= 0.3 ? 'bg-yellow-500' :
                                            'bg-green-500'
                                    }`}
                                style={{ width: `${value * 100}%` }}
                            />
                        </div>
                        <div className={`w-12 text-right text-sm font-medium ${isTopFactor ? 'text-orange-600' : 'text-gray-700'
                            }`}>
                            {(value * 100).toFixed(0)}%
                        </div>
                    </div>
                );
            })}

            {topKeys.length > 0 && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={14} className="text-orange-600" />
                        <span className="text-xs font-medium text-orange-900">Top Contributing Factors</span>
                    </div>
                    <div className="space-y-1">
                        {Object.entries(topFactors).map(([key, value]) => (
                            <div key={key} className="text-xs text-orange-800">
                                <span className="font-medium">{labels[key] || key}:</span> {(value * 100).toFixed(0)}%
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
